/**
 * Helpers compartidos por las pantallas de usage (Doc 25 §F3+F4).
 *
 * Formatters locale-aware (Doc 19) + parseo de Decimal-as-string que
 * vienen del backend.
 */

import type { SupportedLocale } from '../../i18n/config'

const DEFAULT_LOCALE: SupportedLocale = 'es'

/**
 * Decimal del backend → display string en USD canónico americano.
 *
 * Doc 25 §F3 fix — siempre usamos `en-US` independientemente del
 * locale de la UI: `$1,777.78` para valores grandes, `$0.0432` para
 * costos de IA pequeños. Razón: el USD se escribe con punto decimal
 * en TODOS los mercados que lo usan (México, Colombia, Chile, USA,
 * etc.); aplicar formato europeo (`1,7777`) con coma decimal genera
 * confusión — un user mexicano lee "mil setecientos" cuando son
 * "uno con setenta y ocho".
 *
 * Decimales smart:
 *   - valor < $1  → 4 decimales (`$0.0432`) — precisión LLM
 *   - valor ≥ $1  → 2 decimales (`$1.78`)   — legibilidad
 *
 * El parámetro `_locale` queda en la firma por consistencia con los
 * otros formatters; se ignora para USD por la razón explicada.
 */
export function formatUsd(
  amount: string | number | null | undefined,
  _locale: string = DEFAULT_LOCALE,
): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—'
  }
  const numeric = typeof amount === 'string' ? Number(amount) : amount
  if (Number.isNaN(numeric)) return '—'
  const abs = Math.abs(numeric)
  const decimals = abs >= 1 ? 2 : 4
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numeric)
}

/**
 * Entero con separador de miles en formato americano (`1,537,239`).
 *
 * Usamos `en-US` siempre — mismo razonamiento que `formatUsd`: un
 * usuario español que ve `1.537.239` lo lee como "uno con cinco"
 * (decimal europeo) y se confunde. El formato `1,537,239` es
 * inequívoco internacionalmente cuando hablamos de cifras técnicas
 * (tokens, calls, métricas).
 *
 * Las fechas SÍ respetan el locale del usuario — esas son culturales.
 */
export function formatInt(
  n: number | null | undefined,
  _locale: string = DEFAULT_LOCALE,
): string {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('en-US').format(n)
}

/**
 * ISO timestamp → "DD/MM/YYYY HH:mm" locale-aware. Si la fecha es
 * null devuelve "—".
 */
export function formatDateTime(
  iso: string | null | undefined,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * Solo-día → "DD/MM/YYYY" locale-aware. Usado por el desglose
 * `group_by=day` que recibe el día como string ISO (`2026-05-16`).
 */
export function formatDateOnly(
  iso: string | null | undefined,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return '—'
  // `2026-05-16` sin TZ → tratar como local 00:00 para evitar drift.
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

/**
 * Period presets — "últimos N días" → ISO from/to.
 */
export type PeriodPreset = 'last_7d' | 'last_30d' | 'last_90d' | 'this_month'

export function resolvePeriod(preset: PeriodPreset): {
  from: string
  to: string
} {
  const now = new Date()
  const to = now.toISOString()
  if (preset === 'this_month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return { from: start.toISOString(), to }
  }
  const days = preset === 'last_7d' ? 7 : preset === 'last_30d' ? 30 : 90
  const from = new Date(now.getTime() - days * 24 * 3600 * 1000)
  return { from: from.toISOString(), to }
}

/**
 * Heurística simple: el balance está bajo si está debajo del threshold
 * (cuando ambos vienen del backend como string Decimal).
 */
export function isBalanceLow(
  balance_usd: string | null,
  threshold_usd: string | null,
): boolean {
  if (balance_usd === null || threshold_usd === null) return false
  return Number(balance_usd) <= Number(threshold_usd)
}
